import type { ClinicSettings, GfeSettingsView } from "@vaulted/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get } from "../../api";
import { Alert, Button, Checkbox, Field, Loading, Panel, PanelHeader, SelectField, TextAreaField } from "../../components/ui";
import { useCatalog } from "../Catalog";
import { useTags } from "../Clients";
import { SettingsForm, type SettingsResponse, useDraft } from "./common";

interface QualiphyQuestion {
  questionId: number;
  question: string;
  responseType: number;
  responses: { id: number; response: string }[];
}

const KEYS = [
  "gfe.qualiphyEnabled",
  "gfe.qualiphyExamIds",
  "gfe.approvalMonths",
  "gfe.testExamId",
  "gfe.resultTags",
  "gfe.reasonAnswers",
  "gfe.reviewRules",
] as const;

const TAG_ROLES = [
  ["approved", "Approved"],
  ["rejected", "Declined"],
  ["deferred", "Deferred to Qualiphy's medical director"],
  ["flagged", "Approved with restrictions (when staff flag it)"],
] as const;

/** Good faith exams through Qualiphy: the connection, which exam, how long it counts, and result tags. */
export function GfeTab({ data }: { data: SettingsResponse }) {
  const status = useQuery({ queryKey: ["gfe-settings"], queryFn: () => get<GfeSettingsView>("/api/gfe/settings") });
  const tags = useTags();
  const draft = useDraft(data.settings, KEYS, "Saved.");
  const v = draft.value;
  const examId = v["gfe.qualiphyExamIds"][0] ?? null;
  const catalog = useCatalog();
  // Exams in use: the default, and any a service picks.
  const inUse = [...new Set([examId, ...(catalog.data?.items ?? []).filter((i) => i.needsGfe && i.gfeExamId).map((i) => i.gfeExamId)])].filter((x): x is number => !!x);
  const examTitle = (id: number) => status.data?.exams.find((x) => x.id === id)?.title ?? "";
  const [copied, setCopied] = useState(false);
  const lines = (s: string) => s.split("\n").map((x) => x.trim()).filter(Boolean);

  return (
    <div className="flex flex-col gap-6">
      <Panel>
        <PanelHeader title="Qualiphy" description="Good faith exams for services that need one (Catalog → service → Good faith exam)." />
        <div className="flex flex-col gap-3 px-5 pb-5 text-sm">
          {status.isLoading ? (
            <Loading />
          ) : status.data?.connected ? (
            <>
              <span className="font-semibold text-ok">Connected ({status.data.exams.length} exams on the account)</span>
              {status.data.webhookUrl && (
                <div className="flex flex-col gap-1">
                  <span className="text-ink-soft">
                    Results come back to this clinic's private address, sent with every exam. To also get results for exams staff create in Qualiphy's portal, paste it there too (Clinics → Edit → webhook).
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="max-w-full truncate rounded bg-mist px-2 py-1 text-xs">{status.data.webhookUrl.replace(/[0-9a-f]{40}$/, "••••••••")}</code>
                    <Button variant="quiet" onClick={() => void navigator.clipboard?.writeText(status.data!.webhookUrl!).then(() => setCopied(true))}>
                      {copied ? "Copied" : "Copy address"}
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <Alert>{status.data?.problem ?? errorMessage(status.error)}</Alert>
          )}
        </div>
      </Panel>

      <Panel>
        <PanelHeader title="Good faith exams" />
        <SettingsForm onSave={draft.save} saving={draft.saving} error={draft.error} notice={draft.notice} dirty={draft.dirty}>
          <Checkbox
            label="Send Qualiphy exams"
            description="Sent automatically when a visit that needs one is booked (managers can pause that until midnight from the top of any page), or by hand from the visit or the client."
            checked={v["gfe.qualiphyEnabled"]}
            onChange={(on) => draft.set("gfe.qualiphyEnabled", on)}
          />
          <SelectField
            label="Default exam"
            hint="Services set to Clinic default use it (Catalog → service → Good faith exam). Each exam's approval only counts for services using that exam; the medical director's tag counts for all."
            className="max-w-xl"
            value={examId ?? ""}
            onChange={(e) => draft.set("gfe.qualiphyExamIds", e.target.value ? [Number(e.target.value)] : [])}
          >
            <option value="">Pick an exam</option>
            {examId && !status.data?.exams.some((x) => x.id === examId) && <option value={examId}>#{examId}</option>}
            {(status.data?.exams ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                #{x.id} {x.title}
              </option>
            ))}
          </SelectField>
          <Field
            label="An approval counts for (months)"
            type="number"
            min={1}
            max={36}
            className="max-w-48"
            hint="The medical director's tag counts for the same time."
            value={v["gfe.approvalMonths"]}
            onChange={(e) => draft.set("gfe.approvalMonths", Math.max(1, Number(e.target.value) || 1))}
          />
          {status.data?.connected &&
            inUse.map((x) => (
              <ReasonAnswer
                key={x}
                examId={x}
                title={examTitle(x)}
                value={v["gfe.reasonAnswers"][String(x)] ?? null}
                onChange={(a) => {
                  const { [String(x)]: _, ...rest } = v["gfe.reasonAnswers"];
                  draft.set("gfe.reasonAnswers", a ? { ...rest, [String(x)]: a } : rest);
                }}
              />
            ))}
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-semibold text-ink-soft">Tag clients with the result</legend>
            {TAG_ROLES.map(([key, label]) => (
              <SelectField
                key={key}
                label={label}
                className="max-w-md"
                value={v["gfe.resultTags"][key] ?? ""}
                onChange={(e) => draft.set("gfe.resultTags", { ...v["gfe.resultTags"], [key]: e.target.value || null })}
              >
                <option value="">No tag</option>
                {(tags.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </SelectField>
            ))}
          </fieldset>
          <details className="text-sm">
            <summary className="cursor-pointer font-semibold text-muted">When an approval needs staff to read it</summary>
            <div className="mt-2 flex flex-col gap-3">
              <p className="text-ink-soft">The provider answers order questions at the end of the exam. Any answer that doesn't start with one of the "no restrictions" answers asks staff to review it.</p>
              <TextAreaField
                label="Order questions (wording they contain, one per line)"
                rows={3}
                defaultValue={v["gfe.reviewRules"].orderQuestionContains.join("\n")}
                onBlur={(e) => draft.set("gfe.reviewRules", { ...v["gfe.reviewRules"], orderQuestionContains: lines(e.target.value) })}
              />
              <TextAreaField
                label="No-restrictions answers (how they start, one per line)"
                rows={3}
                defaultValue={v["gfe.reviewRules"].unrestrictedAnswers.join("\n")}
                onBlur={(e) => draft.set("gfe.reviewRules", { ...v["gfe.reviewRules"], unrestrictedAnswers: lines(e.target.value) })}
              />
            </div>
          </details>
          {status.data && !status.data.production && (
            <SelectField
              label="Test mode (staging only)"
              hint="While an exam is picked, every exam goes out as that one, in Qualiphy's test state with the name Test Approve: no real provider sees them and they never count for a client. #5359 is Qualiphy's no-charge test exam."
              className="max-w-xl"
              value={v["gfe.testExamId"] ?? ""}
              onChange={(e) => draft.set("gfe.testExamId", e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Off</option>
              {v["gfe.testExamId"] && !status.data.exams.some((x) => x.id === v["gfe.testExamId"]) && <option value={v["gfe.testExamId"]}>#{v["gfe.testExamId"]}</option>}
              {[...status.data.exams]
                .sort((a, b) => (a.id === 5359 ? -1 : b.id === 5359 ? 1 : 0))
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    #{x.id} {x.title}
                  </option>
                ))}
            </SelectField>
          )}
        </SettingsForm>
      </Panel>
    </div>
  );
}

type ReasonAnswerValue = ClinicSettings["gfe.reasonAnswers"][string];

/** One exam's "reason for the visit" answer, sent with every exam so clients aren't asked. */
function ReasonAnswer({ examId, title, value, onChange }: { examId: number; title: string; value: ReasonAnswerValue | null; onChange: (a: ReasonAnswerValue | null) => void }) {
  const questions = useQuery({ queryKey: ["gfe-questions", examId], queryFn: () => get<QualiphyQuestion[]>(`/api/gfe/exams/${examId}/questions`) });
  const q = questions.data?.find((x) => x.questionId === value?.questionId) ?? questions.data?.find((x) => /reason/i.test(x.question));
  if (!q) return null;
  return (
    <SelectField
      label={`#${examId}${title ? ` ${title}` : ""}: answer for "${q.question.trim()}"`}
      hint="Sent with every exam, so clients aren't asked."
      className="max-w-xl"
      value={value?.responseId ?? ""}
      onChange={(e) => {
        const r = q.responses.find((x) => x.id === Number(e.target.value));
        onChange(r ? { questionId: q.questionId, responseId: r.id, label: r.response } : null);
      }}
    >
      <option value="">Let the client answer</option>
      {q.responses.map((r) => (
        <option key={r.id} value={r.id}>
          {r.response}
        </option>
      ))}
    </SelectField>
  );
}
